import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Receipt, CheckCircle2, Download } from 'lucide-react';
import FatturePassive from '../components/fatture/FatturePassive';
import FatturaDettaglioModal from '../components/fatture/FatturaDettaglioModal';
import BozzaWorkspace from '../components/fatture/BozzaWorkspace';
import Modal from '../components/ui/Modal';
import {
  getDaFatturare, getUsciteDaFatturare, creaBozza, creaBozzaVendita, getBozze, scartaBozza,
  aggiungiConferimentiBozza, listFatture, setStatoFattura, annullaFattura, getFatturaXmlBlob,
  type DaFatturareItem, type UscitaDaFatturare, type FatturaResponse, type StatoFattura,
  type BozzaResponse, type TipoFattura,
} from '../api/fatture';
import { useCategorie } from '../hooks/useCategorie';
import { categoriaLabel } from '../lib/categorie';
import { fmtKg } from '../lib/saldo';

const inputCls = 'text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white';

type Tab = 'da-fatturare' | 'bozze' | 'emesse';

function eur(n: number | string): string {
  return Number(n).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

function dataIt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
}

// Canale del documento (V72). Il badge esiste perche' «Bozze» ed «Emesse»
// mescolano servizio e vendita: senza, due righe identiche a video sarebbero
// due documenti con due regimi e due discipline diverse.
const TIPO_BADGE: Record<TipoFattura, string> = {
  servizio: 'bg-stone-bg text-ink-soft',
  vendita: 'bg-sage-light text-sage-dark',
};

function TipoBadge({ tipo }: { tipo: TipoFattura }) {
  const { t } = useTranslation();
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${TIPO_BADGE[tipo]}`}
      data-testid={`tipo-badge-${tipo}`}>
      {t(tipo === 'vendita' ? 'fatturazione.attive.tipo_vendita' : 'fatturazione.attive.tipo_servizio')}
    </span>
  );
}

const STATO_BADGE: Record<StatoFattura, string> = {
  bozza: 'bg-stone-bg text-ink-soft',
  emessa: 'bg-amber-50 text-amber-700',
  pagata: 'bg-sage-light text-sage-dark',
  annullata: 'bg-red-50 text-red-700',
};

function FiltroTipo({ value, onChange }: { value: '' | TipoFattura; onChange: (v: '' | TipoFattura) => void }) {
  const { t } = useTranslation();
  return (
    <select className={inputCls} value={value} data-testid="filtro-tipo"
      onChange={(e) => onChange(e.target.value as '' | TipoFattura)}>
      <option value="">{t('fatturazione.attive.tutti_tipi')}</option>
      <option value="servizio">{t('fatturazione.attive.tipo_servizio')}</option>
      <option value="vendita">{t('fatturazione.attive.tipo_vendita')}</option>
    </select>
  );
}

export default function FatturazionePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Deep-link da un'altra pagina (oggi: le uscite, che linkano la fattura che
  // le ha assorbite). Letto UNA volta all'inizializzazione dello stato, non in
  // un effect: e' il valore iniziale della scheda, non una sincronizzazione.
  const fatturaDeepLink = searchParams.get('fattura');
  // Speculare, sul canale passivo: `?passiva=<id>` apre QUELLA fattura ricevuta
  // (lo usano le uscite a terzi). `?sezione=passive` da solo apre la sezione.
  const passivaDeepLink = searchParams.get('passiva');
  // Terzo della famiglia: `?bozza=<id>` apre direttamente il workspace di QUELLA
  // bozza. Lo usa il banner del dettaglio conferimento — una bozza non ha
  // numero, quindi senza link non c'e' modo di ritrovarla se non a occhio.
  const bozzaDeepLink = searchParams.get('bozza');
  const [tab, setTab] = useState<Tab>(fatturaDeepLink ? 'emesse' : 'da-fatturare');
  const [sezione, setSezione] = useState<'attive' | 'passive'>(
    searchParams.get('sezione') === 'passive' || searchParams.get('passiva') ? 'passive' : 'attive',
  );

  // La bozza aperta (workspace). Quando valorizzata, sostituisce i tab attivi.
  const [openBozzaId, setOpenBozzaId] = useState<string | null>(bozzaDeepLink);
  const [emessa, setEmessa] = useState<FatturaResponse | null>(null);

  // ── Da fatturare ──
  const [items, setItems] = useState<DaFatturareItem[]>([]);
  const [loadingDF, setLoadingDF] = useState(true);
  const [fCliente, setFCliente] = useState('');
  const [fCategoria, setFCategoria] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // V72 — uscite di vendita fatturabili. Sezione SEPARATA e selezione
  // MUTUAMENTE ESCLUSIVA con i conferimenti: il backend rifiuta `righe` e
  // `uscita_ids` insieme, perche' una fattura e' di servizio o di vendita.
  const [uscite, setUscite] = useState<UscitaDaFatturare[]>([]);
  const [selUscite, setSelUscite] = useState<Set<string>>(new Set());
  // 409 «esiste gia' una bozza vendita»: messaggio del BE + link a QUELLA bozza,
  // che il FE ritrova da `getBozze(cliente)` — il 409 porta il testo, non l'id.
  const [bozzaVenditaEsistente, setBozzaVenditaEsistente] = useState<BozzaResponse | null>(null);
  const [creating, setCreating] = useState(false);
  // R5/F1 (bozze multiple): se il cliente ha già bozze aperte, scelta esplicita
  // "Crea nuova / Aggiungi alla esistente" (selettore se più d'una).
  const [sceltaBozze, setSceltaBozze] = useState<BozzaResponse[] | null>(null);
  const [selBozzaId, setSelBozzaId] = useState('');
  // Errori di creazione/aggiunta (es. 409 residuo "conferimenti già agganciati",
  // race con un altro utente): messaggio BE verbatim.
  const [creaError, setCreaError] = useState<string | null>(null);

  const loadDaFatturare = useCallback(() => {
    void (async () => {
      setLoadingDF(true);
      try {
        // In parallelo: sono due endpoint indipendenti e in serie
        // raddoppierebbero l'attesa della scheda.
        const [conf, usc] = await Promise.all([getDaFatturare(), getUsciteDaFatturare()]);
        setItems(conf); setUscite(usc);
      } finally { setLoadingDF(false); }
    })();
  }, []);
  useEffect(() => { loadDaFatturare(); }, [loadDaFatturare]);

  const clienti = useMemo(() => {
    const m = new Map<string, string>();
    items.forEach((i) => m.set(i.customer_id, i.customer_ragione_sociale));
    return [...m.entries()];
  }, [items]);
  const categorie = useMemo(() => [...new Set(items.map((i) => i.categoria))], [items]);
  const { categorie: categorieForm } = useCategorie();   // R4-A: lookup codice → nome_visualizzato

  // Il cliente si blocca sulla PRIMA riga selezionata, da una sezione o
  // dall'altra: le due selezioni non convivono, quindi una sola delle due e'
  // non vuota e il vincolo resta uno solo.
  const lockedCustomer = useMemo(() => {
    const conf = items.find((i) => selected.has(i.conferimento_id));
    if (conf) return conf.customer_id;
    const usc = uscite.find((u) => selUscite.has(u.uscita_id));
    return usc?.customer_id ?? null;
  }, [items, selected, uscite, selUscite]);

  // Quale sezione ha la selezione: l'altra e' disabilitata finche' non si
  // svuota questa. `null` = nessuna selezione, entrambe aperte.
  const sezioneAttiva: 'conferimenti' | 'uscite' | null =
    selected.size > 0 ? 'conferimenti' : selUscite.size > 0 ? 'uscite' : null;

  const nSelezionati = selected.size + selUscite.size;

  const visibleItems = items.filter((i) =>
    (!fCliente || i.customer_id === fCliente) && (!fCategoria || i.categoria === fCategoria),
  );
  // Le uscite non hanno categoria: filtrarle su `fCategoria` le farebbe sparire
  // tutte al primo filtro scelto per i conferimenti.
  const visibleUscite = uscite.filter((u) => !fCliente || u.customer_id === fCliente);

  function toggle(id: string, customer_id: string) {
    setCreaError(null); setBozzaVenditaEsistente(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (lockedCustomer && customer_id !== lockedCustomer) return prev;
      else next.add(id);
      return next;
    });
  }

  function toggleUscita(id: string, customer_id: string) {
    setCreaError(null); setBozzaVenditaEsistente(null);
    setSelUscite((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (lockedCustomer && customer_id !== lockedCustomer) return prev;
      else next.add(id);
      return next;
    });
  }

  function detailDa(e: unknown): string | null {
    const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return typeof detail === 'string' ? detail : null;
  }

  // Click "Crea bozza": con bozze aperte del cliente → scelta esplicita (R5/F1);
  // senza → creazione diretta (comportamento storico).
  async function creaBozzaDaSelezione() {
    if (!lockedCustomer) return;
    if (sezioneAttiva === 'uscite') { await creaBozzaDaUscite(); return; }
    if (selected.size === 0) return;
    setCreating(true); setCreaError(null);
    try {
      // SOLO le bozze di SERVIZIO: la scelta «crea nuova / aggiungi
      // all'esistente» riguarda i conferimenti, e offrire di aggiungerli a una
      // bozza di vendita proporrebbe un'operazione che il backend rifiuta.
      const esistenti = (await getBozze(lockedCustomer)).filter((b) => b.tipo !== 'vendita');
      if (esistenti.length === 0) {
        await doCreaNuova();
      } else {
        setSelBozzaId(esistenti[0].id);
        setSceltaBozze(esistenti);
      }
    } catch (e) {
      setCreaError(detailDa(e) ?? t('fatturazione.bozza.errore_creazione'));
    } finally { setCreating(false); }
  }

  // Vendita: nessuna scelta «nuova vs esistente» — l'indice parziale della V72
  // ammette UNA sola bozza vendita viva per cliente. Se c'e' gia', il BE
  // risponde 409 e qui si mostra il messaggio piu' il link a quella bozza.
  async function creaBozzaDaUscite() {
    setCreating(true); setCreaError(null); setBozzaVenditaEsistente(null);
    try {
      const b = await creaBozzaVendita([...selUscite]);
      setSelUscite(new Set()); setOpenBozzaId(b.id);
    } catch (e) {
      setCreaError(detailDa(e) ?? t('fatturazione.bozza.errore_creazione'));
      if ((e as { response?: { status?: number } })?.response?.status === 409 && lockedCustomer) {
        // Il 409 porta il testo, non l'id: la bozza si ritrova per cliente.
        // Se la ricerca fallisce resta il solo messaggio — un link mancante e'
        // meno peggio di un errore che ne nasconde un altro.
        try {
          const vend = (await getBozze(lockedCustomer)).find((b) => b.tipo === 'vendita');
          if (vend) setBozzaVenditaEsistente(vend);
        } catch { /* nessun link, resta il messaggio del backend */ }
      }
    } finally { setCreating(false); }
  }

  async function doCreaNuova() {
    try {
      const b = await creaBozza([...selected]);
      setSceltaBozze(null); setSelected(new Set()); setOpenBozzaId(b.id);
    } catch (e) {
      // 409 residuo (es. conferimenti già agganciati da un altro utente) → detail verbatim.
      setSceltaBozze(null);
      setCreaError(detailDa(e) ?? t('fatturazione.bozza.errore_creazione'));
    }
  }

  async function doAggiungiEsistente(bozzaId: string) {
    try {
      const b = await aggiungiConferimentiBozza(bozzaId, [...selected]);
      setSceltaBozze(null); setSelected(new Set()); setOpenBozzaId(b.id);
    } catch (e) {
      setSceltaBozze(null);
      setCreaError(detailDa(e) ?? t('fatturazione.bozza.errore_creazione'));
    }
  }

  // ── Bozze aperte ──
  // Filtro di canale condiviso fra «Bozze» ed «Emesse»: e' la stessa domanda
  // («di che documento parliamo»), e due filtri separati si disallineerebbero
  // passando da una scheda all'altra.
  const [fTipo, setFTipo] = useState<'' | TipoFattura>('');
  const [bozze, setBozze] = useState<BozzaResponse[]>([]);
  const loadBozze = useCallback(() => {
    void (async () => { setBozze(await getBozze()); })();
  }, []);
  useEffect(() => { if (tab === 'bozze') loadBozze(); }, [tab, loadBozze]);

  const bozzeVisibili = bozze.filter((b) => !fTipo || b.tipo === fTipo);

  async function scartaDaElenco(id: string) {
    await scartaBozza(id); loadBozze(); loadDaFatturare();
  }

  // ── Emesse ──
  const [fatture, setFatture] = useState<FatturaResponse[]>([]);
  const [fStato, setFStato] = useState<'' | StatoFattura>('');
  const [confirmAnnulla, setConfirmAnnulla] = useState<string | null>(null);
  const [dettaglioId, setDettaglioId] = useState<string | null>(fatturaDeepLink);

  // Entrambi i filtri delle «Emesse» sono lato BACKEND: `GET /fatture` espone
  // `tipo` come query param, quindi non resta un filtro client che si
  // disallineerebbe appena l'elenco verra' paginato. Le «Bozze» restano
  // client-side: `GET /fatture/bozze` non ha il parametro.
  const loadEmesse = useCallback(() => {
    void (async () => {
      setFatture(await listFatture({ ...(fStato ? { stato: fStato } : {}), ...(fTipo ? { tipo: fTipo } : {}) }));
    })();
  }, [fStato, fTipo]);
  useEffect(() => { if (tab === 'emesse') loadEmesse(); }, [tab, loadEmesse]);

  async function cambiaStato(id: string, stato: 'pagata' | 'emessa') { await setStatoFattura(id, stato); loadEmesse(); }
  async function doAnnulla(id: string) { await annullaFattura(id); setConfirmAnnulla(null); loadEmesse(); }
  async function scaricaXml(id: string, numero: string | null) {
    const blob = await getFatturaXmlBlob(id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    // "1/M" → "1-M": '/' non è valido in un filename.
    a.href = url; a.download = `fattura_${(numero ?? id).replace(/\//g, '-')}.xml`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  // ── Callback workspace ──
  function onBozzaEmessa(f: FatturaResponse) {
    setOpenBozzaId(null); setEmessa(f); setTab('emesse'); loadDaFatturare();
  }
  function onBozzaScartata() {
    setOpenBozzaId(null); loadDaFatturare();
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-5">
        <Receipt size={20} className="text-sage" />
        <h1 className="text-xl font-semibold text-ink-dark">{t('fatturazione.attive.titolo')}</h1>
      </div>

      <div className="flex gap-2 mb-5">
        {([['attive', t('fatturazione.attive.sezione_attive')], ['passive', t('fatturazione.attive.sezione_passive')]] as ['attive' | 'passive', string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSezione(k)} data-testid={`sezione-${k}`}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              sezione === k ? 'border-sage bg-sage-light text-sage-dark' : 'border-stone-border text-ink-soft hover:bg-stone-bg'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {sezione === 'passive' && <FatturePassive passivaDeepLink={passivaDeepLink} />}

      {sezione === 'attive' && openBozzaId ? (
        <BozzaWorkspace bozzaId={openBozzaId} onClose={() => setOpenBozzaId(null)}
          onEmessa={onBozzaEmessa} onScartata={onBozzaScartata} />
      ) : sezione === 'attive' && (<>
        {/* Tabs */}
        <div className="flex gap-1 mb-5 border-b border-stone-mid">
          {([['da-fatturare', t('fatturazione.attive.tab_da_fatturare')], ['bozze', t('fatturazione.bozza.tab_bozze')], ['emesse', t('fatturazione.attive.tab_emesse')]] as [Tab, string][]).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === k ? 'border-sage text-sage-dark' : 'border-transparent text-ink-soft hover:text-ink-dark'
              }`}>
              {label}{k === 'da-fatturare' && nSelezionati > 0 ? ` (${nSelezionati})` : ''}
            </button>
          ))}
        </div>

        {/* ── TAB: Da fatturare ── */}
        {tab === 'da-fatturare' && (
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              <select className={inputCls} value={fCliente} onChange={(e) => setFCliente(e.target.value)}>
                <option value="">{t('fatturazione.attive.tutti_clienti')}</option>
                {clienti.map(([id, rs]) => <option key={id} value={id}>{rs}</option>)}
              </select>
              <select className={inputCls} value={fCategoria} onChange={(e) => setFCategoria(e.target.value)}>
                <option value="">{t('fatturazione.attive.tutte_categorie')}</option>
                {categorie.map((c) => <option key={c} value={c}>{categoriaLabel(c, categorieForm)}</option>)}
              </select>
              <button type="button" disabled={nSelezionati === 0 || creating} onClick={creaBozzaDaSelezione}
                data-testid="btn-crea-bozza"
                className="ml-auto px-3 py-1.5 text-sm font-medium rounded bg-sage text-white disabled:opacity-40 disabled:cursor-not-allowed">
                {creating ? t('fatturazione.bozza.creazione') : t('fatturazione.bozza.crea_bozza', { n: nSelezionati })}
              </button>
            </div>
            {creaError && (
              <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid="crea-bozza-error">
                {creaError}
              </div>
            )}
            {bozzaVenditaEsistente && (
              <div className="mb-3 px-3 py-2 bg-stone-bg border border-stone-mid rounded text-sm" data-testid="link-bozza-vendita">
                <button type="button" onClick={() => setOpenBozzaId(bozzaVenditaEsistente.id)}
                  data-testid="btn-apri-bozza-vendita" className="text-sage-dark font-medium hover:underline">
                  {t('fatturazione.attive.apri_bozza_vendita')}
                </button>
              </div>
            )}
            {lockedCustomer && <p className="text-xs text-ink-soft mb-2">{t('fatturazione.attive.locked_customer')}</p>}
            {sezioneAttiva && (
              <p className="text-xs text-ink-soft mb-2" data-testid="nota-mutua-esclusione">
                {t(sezioneAttiva === 'conferimenti'
                  ? 'fatturazione.attive.esclusione_conferimenti'
                  : 'fatturazione.attive.esclusione_uscite')}
              </p>
            )}
            <h3 className="text-sm font-semibold text-ink-dark mb-2">{t('fatturazione.attive.sez_conferimenti')}</h3>
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-bg">
                  <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                    <th className="px-3 py-3 w-8"></th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_conferimento')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_cliente')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_categoria')}</th>
                    <th className="px-3 py-3 text-right">{t('fatturazione.attive.col_peso_netto')}</th>
                    <th className="px-3 py-3 text-right">{t('fatturazione.attive.col_tariffa')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingDF ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.attive.caricamento')}</td></tr>
                  ) : visibleItems.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.attive.vuoto_da_fatturare')}</td></tr>
                  ) : visibleItems.map((it) => {
                    const disabled = sezioneAttiva === 'uscite'
                      || (!!lockedCustomer && it.customer_id !== lockedCustomer && !selected.has(it.conferimento_id));
                    return (
                      <tr key={it.conferimento_id} className={`border-t border-stone-mid/60 ${disabled ? 'opacity-40' : 'hover:bg-stone-bg/50'}`}>
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={selected.has(it.conferimento_id)} disabled={disabled}
                            data-testid={`chk-${it.conferimento_id}`} onChange={() => toggle(it.conferimento_id, it.customer_id)} />
                        </td>
                        <td className="px-3 py-2.5 font-medium">{it.numero ?? '—'}</td>
                        <td className="px-3 py-2.5">{it.customer_ragione_sociale}</td>
                        <td className="px-3 py-2.5">{categoriaLabel(it.categoria, categorieForm)}</td>
                        <td className="px-3 py-2.5 text-right">{it.peso_netto_kg ?? '—'}</td>
                        <td className="px-3 py-2.5 text-right">{it.tariffa_unitaria_eur_kg ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Uscite in vendita (V72) ── */}
            <h3 className="text-sm font-semibold text-ink-dark mt-5 mb-2">{t('fatturazione.attive.sez_uscite')}</h3>
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden" data-testid="sez-uscite-vendita">
              <table className="w-full text-sm">
                <thead className="bg-stone-bg">
                  <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                    <th className="px-3 py-3 w-8"></th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_uscita')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_cliente')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_rifiuto')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_data_partenza')}</th>
                    <th className="px-3 py-3 text-right">{t('fatturazione.attive.col_kg')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_regime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingDF ? (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.attive.caricamento')}</td></tr>
                  ) : visibleUscite.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.attive.vuoto_uscite')}</td></tr>
                  ) : visibleUscite.map((u) => {
                    const disabled = sezioneAttiva === 'conferimenti'
                      || (!!lockedCustomer && u.customer_id !== lockedCustomer && !selUscite.has(u.uscita_id));
                    return (
                      <tr key={u.uscita_id} className={`border-t border-stone-mid/60 ${disabled ? 'opacity-40' : 'hover:bg-stone-bg/50'}`}>
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={selUscite.has(u.uscita_id)} disabled={disabled}
                            data-testid={`chk-uscita-${u.uscita_id}`} onChange={() => toggleUscita(u.uscita_id, u.customer_id)} />
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs font-medium">{u.codice}
                          <span className="block text-[11px] text-ink-faint font-sans">{t('fatturazione.attive.fir_label', { n: u.numero_fir })}</span>
                        </td>
                        <td className="px-3 py-2.5">{u.customer_ragione_sociale}</td>
                        <td className="px-3 py-2.5">{u.descrizione_rifiuto}
                          <span className="block text-[11px] text-ink-faint font-mono">{u.cer_code}</span>
                        </td>
                        <td className="px-3 py-2.5">{dataIt(u.data_partenza)}</td>
                        <td className="px-3 py-2.5 text-right">{fmtKg(u.peso_kg)}</td>
                        <td className="px-3 py-2.5">
                          {/* Regime del CATALOGO: e' il default con cui nascerebbe la riga,
                              non ancora uno snapshot — per un privato il BE lo forzera'
                              a ordinaria al momento della bozza. */}
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-stone-bg text-ink-soft"
                            data-testid={`regime-catalogo-${u.uscita_id}`}>
                            {t(u.regime_iva_catalogo === 'reverse_charge'
                              ? 'settings.cer_rifiuti.regime_reverse_charge'
                              : 'settings.cer_rifiuti.regime_iva_ordinaria')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB: Bozze aperte ── */}
        {tab === 'bozze' && (
          <>
          <div className="flex flex-wrap gap-2 mb-4"><FiltroTipo value={fTipo} onChange={setFTipo} /></div>
          <div className="bg-white border border-stone-mid rounded-xl overflow-hidden" data-testid="elenco-bozze">
            <table className="w-full text-sm">
              <thead className="bg-stone-bg">
                <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  <th className="px-3 py-3">{t('fatturazione.attive.col_cliente')}</th>
                  <th className="px-3 py-3">{t('fatturazione.attive.col_tipo')}</th>
                  <th className="px-3 py-3">{t('fatturazione.bozza.col_creata')}</th>
                  <th className="px-3 py-3 text-right">{t('fatturazione.bozza.col_righe')}</th>
                  <th className="px-3 py-3 text-right">{t('fatturazione.bozza.totale')}</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {bozzeVisibili.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.bozza.nessuna_bozza')}</td></tr>
                ) : bozzeVisibili.map((b) => (
                  <tr key={b.id} onClick={() => setOpenBozzaId(b.id)}
                    className="border-t border-stone-mid/60 hover:bg-stone-bg/50 cursor-pointer" data-testid={`bozza-row-${b.id}`}>
                    <td className="px-3 py-2.5 font-medium">{b.customer_ragione_sociale}</td>
                    <td className="px-3 py-2.5"><TipoBadge tipo={b.tipo} /></td>
                    <td className="px-3 py-2.5" data-testid={`bozza-creata-${b.id}`}>{dataIt(b.created_at)}</td>
                    {/* Conteggio del canale che la bozza usa davvero: su una
                        vendita `conferimenti` e' vuoto per costruzione. */}
                    <td className="px-3 py-2.5 text-right">{b.tipo === 'vendita' ? b.uscite.length : b.conferimenti.length}</td>
                    <td className="px-3 py-2.5 text-right">{eur(b.importo_totale)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {/* stopPropagation: lo scarto non deve aprire la bozza (riga cliccabile). */}
                      <button type="button" onClick={(e) => { e.stopPropagation(); scartaDaElenco(b.id); }} data-testid={`btn-scarta-bozza-${b.id}`}
                        className="text-xs text-red-700 hover:underline">{t('fatturazione.bozza.scarta')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* ── TAB: Emesse ── */}
        {tab === 'emesse' && (
          <div>
            {emessa && (
              <div className="mb-4 bg-white border border-stone-mid rounded-xl p-4 max-w-2xl" data-testid="banner-emessa">
                <div className="flex items-center gap-2 text-sage-dark mb-1">
                  <CheckCircle2 size={18} />
                  <span className="font-semibold" data-testid="emessa-numero-ufficiale">{t('fatturazione.attive.fattura_emessa', { numero: emessa.numero_fattura ?? '—' })}</span>
                </div>
                <p className="text-sm text-ink-mid">{t('fatturazione.attive.totale_conf', { totale: eur(emessa.importo_totale), n: emessa.conferimento_ids.length })}</p>
                {emessa.xml_path && (
                  <button type="button" data-testid="btn-scarica-xml" onClick={() => scaricaXml(emessa.id, emessa.numero_fattura)}
                    className="mt-2 inline-flex items-center gap-1 text-sm text-sage-dark hover:underline">
                    <Download size={14} />{t('fatturazione.attive.scarica_xml')}
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
              <select className={inputCls} value={fStato} onChange={(e) => setFStato(e.target.value as '' | StatoFattura)}>
                <option value="">{t('fatturazione.attive.tutti_stati')}</option>
                <option value="emessa">{t('fatturazione.attive.stato.emessa')}</option>
                <option value="pagata">{t('fatturazione.attive.stato.pagata')}</option>
                <option value="annullata">{t('fatturazione.attive.stato.annullata')}</option>
              </select>
              {/* Lo stato lo filtra il BACKEND (parametro della GET), il canale
                  il client: `GET /fatture` non espone un filtro `tipo` e
                  aggiungerlo sarebbe backend, fuori da questa fetta. */}
              <FiltroTipo value={fTipo} onChange={setFTipo} />
            </div>
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-bg">
                  <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                    <th className="px-3 py-3">{t('fatturazione.attive.col_numero')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_tipo')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_data')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_scadenza')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_stato')}</th>
                    <th className="px-3 py-3 text-right">{t('fatturazione.attive.col_totale')}</th>
                    <th className="px-3 py-3">{t('fatturazione.attive.col_xml')}</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {fatture.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.attive.nessuna_fattura')}</td></tr>
                  ) : fatture.map((f) => (
                    <tr key={f.id} className="border-t border-stone-mid/60 hover:bg-stone-bg/50">
                      <td className="px-3 py-2.5 font-medium" data-testid={`fattura-numero-${f.id}`}>
                        <button type="button" onClick={() => setDettaglioId(f.id)} className="text-sage-dark hover:underline" data-testid={`btn-dettaglio-${f.id}`}>
                          {f.numero_fattura ?? f.numero_interno ?? t('fatturazione.attive.dettaglio')}
                        </button>
                        <span className="block text-[11px] text-ink-faint font-normal">
                          {t('fatturazione.attive.int_label', { n: f.numero_interno ?? '—' })}{f.progressivo_invio ? t('fatturazione.attive.prog_label', { p: f.progressivo_invio }) : ''}
                        </span>
                      </td>
                      <td className="px-3 py-2.5"><TipoBadge tipo={f.tipo} /></td>
                      <td className="px-3 py-2.5">{new Date(f.data_emissione).toLocaleDateString('it-IT')}</td>
                      <td className="px-3 py-2.5" data-testid={`fattura-scadenza-${f.id}`}>{dataIt(f.data_scadenza)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATO_BADGE[f.stato]}`}>{t(`fatturazione.attive.stato.${f.stato}`, { defaultValue: f.stato })}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right">{eur(f.importo_totale)}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {f.xml_path ? (
                          <button type="button" data-testid="btn-scarica-xml" onClick={() => scaricaXml(f.id, f.numero_fattura)}
                            className="inline-flex items-center gap-1 text-sage-dark hover:underline">
                            <Download size={13} />{t('fatturazione.attive.scarica_xml')}
                          </button>
                        ) : <span className="text-ink-soft">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {f.stato === 'emessa' && (
                          <>
                            <button type="button" onClick={() => cambiaStato(f.id, 'pagata')} className="text-xs text-sage-dark hover:underline mr-3">{t('fatturazione.attive.segna_pagata')}</button>
                            {confirmAnnulla === f.id ? (
                              <span className="text-xs">
                                {t('fatturazione.attive.eliminare')}{' '}
                                <button type="button" data-testid={`btn-annulla-${f.id}`} onClick={() => doAnnulla(f.id)} className="text-red-700 font-medium hover:underline">{t('fatturazione.attive.si')}</button>{' / '}
                                <button type="button" onClick={() => setConfirmAnnulla(null)} className="text-ink-soft hover:underline">{t('fatturazione.attive.no')}</button>
                              </span>
                            ) : (
                              <button type="button" data-testid={`btn-annulla-${f.id}`} onClick={() => setConfirmAnnulla(f.id)} className="text-xs text-red-700 hover:underline">{t('fatturazione.attive.annulla')}</button>
                            )}
                          </>
                        )}
                        {f.stato === 'pagata' && (
                          <button type="button" onClick={() => cambiaStato(f.id, 'emessa')} className="text-xs text-ink-soft hover:underline">{t('fatturazione.attive.annulla_pagamento')}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-ink-faint mt-2">{t('fatturazione.attive.nota_annulla')}</p>
          </div>
        )}
        {dettaglioId && <FatturaDettaglioModal fatturaId={dettaglioId} onClose={() => setDettaglioId(null)} />}

        {/* R5/F1: scelta esplicita quando il cliente ha già bozze aperte. */}
        {sceltaBozze && (
          <Modal
            title={t('fatturazione.bozza.scelta_titolo')}
            subtitle={sceltaBozze[0].customer_ragione_sociale}
            onClose={() => setSceltaBozze(null)}
            testid="modal-scelta-bozza"
            footer={(
              <>
                <button type="button" onClick={() => setSceltaBozze(null)}
                  className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors">
                  {t('common.azioni.annulla')}
                </button>
                <button type="button" onClick={() => void doCreaNuova()} data-testid="btn-scelta-crea-nuova"
                  className="px-4 py-2 text-sm font-medium text-sage-dark border border-sage rounded-lg hover:bg-sage-light transition-colors">
                  {t('fatturazione.bozza.crea_nuova')}
                </button>
                <button type="button" onClick={() => void doAggiungiEsistente(selBozzaId)} data-testid="btn-scelta-aggiungi"
                  className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors">
                  {t('fatturazione.bozza.aggiungi_esistente')}
                </button>
              </>
            )}
          >
            <p className="text-sm text-ink-mid">{t('fatturazione.bozza.scelta_testo', { n: sceltaBozze.length })}</p>
            {sceltaBozze.length > 1 ? (
              <div>
                <label className="block text-xs font-medium text-ink-soft mb-1">{t('fatturazione.bozza.scelta_selettore')}</label>
                <select className={`${inputCls} w-full cursor-pointer`} value={selBozzaId} data-testid="scelta-bozza-select"
                  onChange={(e) => setSelBozzaId(e.target.value)}>
                  {sceltaBozze.map((b) => (
                    <option key={b.id} value={b.id}>
                      {t('fatturazione.bozza.scelta_opzione', {
                        data: dataIt(b.created_at), n: b.conferimenti.length, totale: eur(b.importo_totale),
                      })}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="text-sm text-ink-dark" data-testid="scelta-bozza-unica">
                {t('fatturazione.bozza.scelta_opzione', {
                  data: dataIt(sceltaBozze[0].created_at), n: sceltaBozze[0].conferimenti.length,
                  totale: eur(sceltaBozze[0].importo_totale),
                })}
              </p>
            )}
          </Modal>
        )}
      </>)}
    </div>
  );
}
